import React, { useContext, useEffect, useState } from 'react';
import styles from './style.module.scss';
import Image from 'next/image';
import Button, { SECONDARY_THEME } from 'components/control/button';
import {
  CONNECTION_REQUEST_MOBILE_MODAL,
  CONNECTION_REQUEST_POPUP_MODAL,
  IModalContext,
  ModalContext,
  useThemeContext,
} from 'contexts';
import useSizeClass from 'hooks/use-size-class';
import { fetchAgentDetailsByNumericKey } from 'utils/agent-endpoint';
import { isExpTheme, isThemeExpInService } from 'utils/tenant/which-tenant';
import CopyButton from 'components/control/copy-button';

import type { ListingInfoType } from 'components/connection-request';

interface Milnet {
  milnetAssociation?: string;
  status?: 'Active Duty' | 'Reserve' | 'Veteran' | 'Retired' | 'Spouse'; // https://github.com/exprealty-com/agent-admin/blob/staging/app/models/milnet_application.rb#L30
}
export interface ListingAgentInfo {
  id: string;
  firstName: string;
  lastName: string;
  cityState?: string;
  email?: string;
  photo?: string;
  phoneNumber?: string;
  milnet?: Milnet;
  title?: string;
}

export default function ListingAgent({ listing }: { listing: ListingInfoType }) {
  const { openModal } = useContext(ModalContext) as IModalContext;
  const { themeName } = useThemeContext();
  const [agentInfo, setAgentInfo] = useState<ListingAgentInfo | null>();
  const agentPic = agentInfo?.photo ? agentInfo?.photo : '/next/assets/images/agent-search/agent-default-profile-pic.png';
  const isMobile = useSizeClass({ max: 980 });

  const fetchAgentInfo = async () => {
    const res = await fetchAgentDetailsByNumericKey(listing?.expAgentId);
    if (res){
      if (isThemeExpInService(themeName) && res?.milnet?.milnetAssociation){
        setAgentInfo({
          ...res,
          title: res?.milnet?.milnetAssociation,
        });
      } else {
        setAgentInfo(res);
      }
    }
  };

  useEffect(() => {
    if (isExpTheme(themeName) && listing?.expAgentId) fetchAgentInfo();
  }, []);

  const onClick = () => {
    if (isMobile) {
      openModal(CONNECTION_REQUEST_MOBILE_MODAL, { listing, agentInfo });
    } else {
      openModal(CONNECTION_REQUEST_POPUP_MODAL, { listing, agentInfo });
    }
  };

  const renderMobile = () => ( // Mobile structure is completely different
    <div className={styles['component-mobile']}>
      <div className={styles['content-container']}>
        <div className={styles['top-section']}>
          <div className={styles['top-left-section']}>
            <Image src={agentPic} alt={agentInfo?.firstName} className={styles['avatar-large']} width={60} height={60}/>
          </div>
          <div className={styles['top-right-section']}>
            <p className={styles['agent-name']}>{agentInfo?.firstName} {agentInfo?.lastName}</p>
            <p className={styles['agent-location']}>{agentInfo?.title || 'eXp Realty Agent'}</p>
            <p className={styles['agent-location']}>{agentInfo?.cityState}</p>
            {agentInfo?.email &&
              <div className={styles['copiable-text']}>
                <p>{agentInfo.email}</p>
                <CopyButton content={agentInfo.email} tooltipText={'Email Copied'} />
              </div>
            }
            {agentInfo?.phoneNumber &&
              <div className={styles['copiable-text']}>
                <p>{agentInfo.phoneNumber}</p>
                <CopyButton content={agentInfo.phoneNumber} tooltipText={'Phone Copied'} />
              </div>
            }
          </div>
        </div>
        <div className={styles['bottom-section']}>
          <Button className={styles['cta-button']} onClick={onClick} theme={SECONDARY_THEME} label={'Contact Agent'} />
        </div>
      </div>
    </div>
  );

  return (agentInfo && <>
    {isMobile ? renderMobile() :
      <div className={styles.component}>
        <div className={styles['content-container']}>
          <div className={styles['top-section']}>
            <Image src={agentPic} alt={agentInfo?.firstName} className={styles['avatar-large']} width={60} height={60}/>
            <div className={styles['agent-info']}>
              <p className={styles['agent-name']}>{agentInfo?.firstName} {agentInfo?.lastName}</p>
            </div>
          </div>
          <div className={styles['bottom-section']}>
            <div className={styles['section-1']}>
              <p>{agentInfo?.title || 'eXp Realty Agent'}</p>
              <p>{agentInfo.cityState}</p>
            </div>
            <div className={styles['section-2']}>
              {agentInfo?.email &&
                <div className={styles['copiable-text']}>
                  <p>{agentInfo.email}</p>
                  <CopyButton className={styles['copy-button']} content={agentInfo.email} tooltipText={'Email Copied'} />
                </div>
              }
              {agentInfo?.phoneNumber &&
                <div className={styles['copiable-text']}>
                  <p>{agentInfo.phoneNumber}</p>
                  <CopyButton content={agentInfo.phoneNumber} tooltipText={'Phone Copied'} />
                </div>
              }
            </div>
            <div className={styles['section-3']}>
              <Button className={styles['cta-button']} onClick={onClick} theme={SECONDARY_THEME} label={'Contact Agent'} />
            </div>
          </div>
        </div>
      </div>
    }</>
  );
}
